import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { PlusIcon, ArrowRightIcon, TrashIcon, EyeIcon, EyeSlashIcon, UsersThreeIcon, CurrencyDollarIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  notes: "",
  plan: "trial",
  master_email: "",
  master_password: "",
  master_name: "",
};

export default function SuperAdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [subs, setSubs] = useState({});  // company_id -> sub row
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [overrideCompany, setOverrideCompany] = useState(null);   // company whose billing we're editing
  const [overrideForm, setOverrideForm] = useState({ billing_mode: "subscription", plan_lookup_key: "repfleet_pro_monthly", custom_price_amount: "", extend_trial_days: "" });
  const [overrideSaving, setOverrideSaving] = useState(false);

  const openOverride = (c) => {
    const s = subs[c.id] || {};
    const existing = s.overrides || {};
    setOverrideForm({
      billing_mode: s.billing_mode || "subscription",
      plan_lookup_key: existing.plan_lookup_key || s.plan_lookup_key || "repfleet_pro_monthly",
      custom_price_amount: existing.custom_price_amount ?? "",
      extend_trial_days: "",
    });
    setOverrideCompany(c);
  };

  const saveOverride = async () => {
    if (!overrideCompany) return;
    setOverrideSaving(true);
    try {
      const payload = {
        billing_mode: overrideForm.billing_mode,
        plan_lookup_key: overrideForm.plan_lookup_key || null,
        custom_price_amount: overrideForm.custom_price_amount === "" ? null : Number(overrideForm.custom_price_amount),
        extend_trial_days: overrideForm.extend_trial_days === "" ? null : Number(overrideForm.extend_trial_days),
      };
      await api.post(`/super-admin/companies/${overrideCompany.id}/billing-override`, payload);
      toast.success(`Billing updated for ${overrideCompany.name}`);
      setOverrideCompany(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setOverrideSaving(false); }
  };

  const clearOverride = async () => {
    if (!overrideCompany) return;
    if (!window.confirm(`Clear all billing overrides for ${overrideCompany.name}? Their next checkout will use the standard price.`)) return;
    setOverrideSaving(true);
    try {
      await api.delete(`/super-admin/companies/${overrideCompany.id}/billing-override`);
      toast.success("Overrides cleared");
      setOverrideCompany(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    } finally { setOverrideSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        api.get("/super-admin/companies"),
        api.get("/super-admin/subscriptions").catch(() => ({ data: [] })),
      ]);
      setCompanies(cRes.data);
      const map = {};
      (sRes.data || []).forEach((s) => { map[s.company_id] = s; });
      setSubs(map);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load companies");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/super-admin/companies", form);
      toast.success(`${form.name} created — master login: ${form.master_email}`);
      setOpenCreate(false);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/super-admin/companies/${c.id}/status`, { is_active: c.is_active === false });
      toast.success(`${c.name} is now ${c.is_active === false ? "active" : "inactive"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This will REMOVE all their trucks, users, and data. Cannot be undone.`)) return;
    try {
      await api.delete(`/super-admin/companies/${c.id}`);
      toast.success(`${c.name} deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const impersonate = async (c) => {
    try {
      const { data } = await api.post("/super-admin/impersonate", { company_id: c.id });
      if (data.token) sessionStorage.setItem("fleet_token", data.token);
      toast.success(`Now viewing as ${c.name}`);
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div data-testid="sa-companies">
      <div className="border-b border-slate-300 px-8 py-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-600 font-bold mb-1">/// Tenants</div>
          <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Companies</h1>
          <p className="text-sm text-slate-400 mt-1">Manage the trucking companies subscribed to Rep-Fleet.</p>
        </div>
        <Button
          onClick={() => setOpenCreate(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
          data-testid="sa-add-company"
        >
          <PlusIcon size={16} weight="bold" className="mr-2" /> New Company
        </Button>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-300 rounded-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Company</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Contact</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Plan</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Subscription</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Users</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Fleet</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400">Status</th>
                <th className="px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-slate-400 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`sa-company-row-${c.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <div className="font-mono text-[10px] text-slate-500">{c.id.slice(0, 8)}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    <div className="text-xs">{c.contact_name || "—"}</div>
                    <div className="font-mono text-[11px] text-slate-500">{c.contact_email || ""}</div>
                  </td>
                  <td className="px-6 py-4">
                    {subs[c.id]?.billing_mode === "free" ? (
                      <span className="text-[10px] font-mono text-slate-400">—</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase bg-blue-100 text-blue-700 border border-blue-500/40 rounded-sm">
                        {c.plan}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <SubscriptionBadge sub={subs[c.id]} />
                  </td>
                  <td className="px-6 py-4 tabular-nums text-slate-700">{c.user_count}</td>
                  <td className="px-6 py-4 tabular-nums text-slate-700">
                    {c.truck_count} <span className="text-slate-500 text-xs">trucks</span> ·{" "}
                    {c.trailer_count} <span className="text-slate-500 text-xs">trailers</span>
                  </td>
                  <td className="px-6 py-4">
                    <Switch
                      checked={c.is_active !== false}
                      onCheckedChange={() => toggleActive(c)}
                      data-testid={`sa-toggle-${c.id}`}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openOverride(c)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                        data-testid={`sa-billing-${c.id}`}
                        title="Set custom price or extend trial for this company"
                      >
                        <CurrencyDollarIcon size={14} weight="bold" /> Billing
                      </button>
                      <button
                        onClick={() => impersonate(c)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-800"
                        data-testid={`sa-impersonate-${c.id}`}
                        title="Log in as this company's admin"
                      >
                        <UsersThreeIcon size={14} weight="bold" /> Impersonate <ArrowRightIcon size={12} weight="bold" />
                      </button>
                      <button
                        onClick={() => del(c)}
                        className="text-xs font-semibold text-red-400 hover:text-red-300 ml-2"
                        data-testid={`sa-delete-${c.id}`}
                        title="Delete company + all data"
                      >
                        <TrashIcon size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && companies.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-slate-500">
                    No companies yet. Click <span className="text-blue-600">New Company</span> to create your first tenant.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE COMPANY DIALOG */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="sa-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">New Company + Master Login</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200 pb-1">Company</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Company Name *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Acme Trucking Co."
                  className="mt-1.5 rounded-sm"
                  data-testid="sa-form-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Contact Name</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Contact Email</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Contact Phone</Label>
                <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1.5 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Plan</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="sa-form-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="growth">Growth</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Address</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 rounded-sm" />
              </div>
            </div>

            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 font-bold border-b border-slate-200 pb-1 pt-4">Master Login (Owner)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Full Name *</Label>
                <Input
                  required
                  value={form.master_name}
                  onChange={(e) => setForm({ ...form, master_name: e.target.value })}
                  placeholder="e.g. John Owner"
                  className="mt-1.5 rounded-sm"
                  data-testid="sa-form-master-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Email *</Label>
                <Input
                  type="email"
                  required
                  value={form.master_email}
                  onChange={(e) => setForm({ ...form, master_email: e.target.value })}
                  placeholder="owner@acme.com"
                  className="mt-1.5 rounded-sm font-mono"
                  data-testid="sa-form-master-email"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Password *</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPw ? "text" : "password"}
                    required
                    minLength={4}
                    value={form.master_password}
                    onChange={(e) => setForm({ ...form, master_password: e.target.value })}
                    placeholder="min 4 characters"
                    className="rounded-sm font-mono pr-10"
                    data-testid="sa-form-master-password"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800">
                    {showPw ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />}
                  </button>
                </div>
                <div className="text-xs text-slate-500 mt-1">The company&apos;s owner will use these credentials to log in as their Admin.</div>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} className="rounded-sm">Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
                data-testid="sa-form-save"
              >
                {saving ? "Creating..." : "Create Company"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* BILLING OVERRIDE DIALOG */}
      <Dialog open={!!overrideCompany} onOpenChange={(v) => !v && setOverrideCompany(null)}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="sa-override-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <CurrencyDollarIcon size={18} weight="fill" className="text-emerald-700" />
              Billing overrides — {overrideCompany?.name}
            </DialogTitle>
          </DialogHeader>
          {overrideCompany && (
            <div className="space-y-4">
              <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2">
                Current status: <b>{subs[overrideCompany.id]?.status || "none"}</b>
                {subs[overrideCompany.id]?.trial_end && (
                  <> · trial ends <b>{new Date(subs[overrideCompany.id].trial_end).toLocaleDateString()}</b></>
                )}
              </div>

              <div>
                <Label>Billing mode</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setOverrideForm((f) => ({ ...f, billing_mode: "subscription" }))}
                    className={`border-2 rounded-sm px-3 py-3 text-left transition-colors ${overrideForm.billing_mode === "subscription" ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white hover:border-slate-400"}`}
                    data-testid="override-mode-subscription"
                  >
                    <div className="font-bold text-sm">💳 Subscription</div>
                    <div className="text-[11px] text-slate-600 font-mono mt-0.5">Must pay via Stripe to unlock</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOverrideForm((f) => ({ ...f, billing_mode: "free" }))}
                    className={`border-2 rounded-sm px-3 py-3 text-left transition-colors ${overrideForm.billing_mode === "free" ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-slate-400"}`}
                    data-testid="override-mode-free"
                  >
                    <div className="font-bold text-sm">🎁 Free</div>
                    <div className="text-[11px] text-slate-600 font-mono mt-0.5">Fully unlocked, no billing</div>
                  </button>
                </div>
              </div>

              {overrideForm.billing_mode === "subscription" && (
                <>
              <div>
                <Label>Plan</Label>
                <Select
                  value={overrideForm.plan_lookup_key}
                  onValueChange={(v) => setOverrideForm((f) => ({ ...f, plan_lookup_key: v }))}
                >
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="override-plan-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="repfleet_basic_monthly">Basic ($49/mo standard)</SelectItem>
                    <SelectItem value="repfleet_pro_monthly">Pro ($129/mo standard)</SelectItem>
                    <SelectItem value="repfleet_enterprise_monthly">Enterprise ($299/mo standard)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Custom monthly price ($) — leave blank for standard</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={overrideForm.custom_price_amount}
                  onChange={(e) => setOverrideForm((f) => ({ ...f, custom_price_amount: e.target.value }))}
                  className="rounded-sm font-mono mt-1.5"
                  placeholder="e.g. 79"
                  data-testid="override-price-input"
                />
                <div className="text-[10px] font-mono text-slate-500 mt-1">
                  If set, this company pays this amount every month regardless of plan. Enter 0 to clear.
                </div>
              </div>

              <div>
                <Label>Extend trial by (days)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={overrideForm.extend_trial_days}
                  onChange={(e) => setOverrideForm((f) => ({ ...f, extend_trial_days: e.target.value }))}
                  className="rounded-sm font-mono mt-1.5"
                  placeholder="e.g. 30"
                  data-testid="override-trial-input"
                />
                <div className="text-[10px] font-mono text-slate-500 mt-1">
                  Adds N days to their current trial end (or starts a new N-day trial if they haven&apos;t subscribed yet).
                </div>
              </div>
                </>
              )}

              {overrideForm.billing_mode === "free" && (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 rounded-sm px-3 py-2.5">
                  <b>🎁 Free mode:</b> This company will have every feature unlocked without any Stripe subscription or trial gate. Ideal for partners, pilot customers, or your own internal accounts. You can flip it back to Subscription anytime.
                </div>
              )}

              {(subs[overrideCompany.id]?.overrides || {}).custom_price_amount != null && (
                <div className="text-xs bg-amber-50 border border-amber-300 rounded-sm px-3 py-2 font-mono">
                  Current override: ${subs[overrideCompany.id].overrides.custom_price_amount}/mo
                  {subs[overrideCompany.id].overrides.trial_end_at && (<> · trial ends {new Date(subs[overrideCompany.id].overrides.trial_end_at).toLocaleDateString()}</>)}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 flex-row">
            <Button variant="outline" onClick={clearOverride} disabled={overrideSaving} className="rounded-sm text-red-600" data-testid="override-clear-btn">
              Clear all overrides
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setOverrideCompany(null)} disabled={overrideSaving} className="rounded-sm">
              Cancel
            </Button>
            <Button onClick={saveOverride} disabled={overrideSaving} className="bg-emerald-700 hover:bg-emerald-800 rounded-sm" data-testid="override-save-btn">
              {overrideSaving ? "Saving…" : "Save override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubscriptionBadge({ sub }) {
  if (sub?.billing_mode === "free") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border rounded-sm w-fit bg-emerald-100 text-emerald-800 border-emerald-400">
        🎁 Free
      </span>
    );
  }
  if (!sub || sub.status === "none" || !sub.status) {
    return <span className="text-[10px] font-mono text-slate-500">— none</span>;
  }
  const toneMap = {
    trialing: "bg-blue-100 text-blue-800 border-blue-400",
    active: "bg-emerald-100 text-emerald-800 border-emerald-400",
    past_due: "bg-amber-100 text-amber-800 border-amber-400",
    canceled: "bg-red-100 text-red-800 border-red-400",
    incomplete: "bg-slate-100 text-slate-700 border-slate-400",
    unpaid: "bg-red-100 text-red-800 border-red-400",
  };
  const cls = toneMap[sub.status] || "bg-slate-100 text-slate-700 border-slate-400";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase border rounded-sm w-fit ${cls}`}>
        {sub.status.replace("_", " ")}
      </span>
      {sub.plan_name && <span className="text-[10px] font-mono text-slate-500">{sub.plan_name}{sub.cancel_at_period_end ? " · ending" : ""}</span>}
    </div>
  );
}

